import {
  Color,
  Group,
  Mesh,
  MeshStandardNodeMaterial,
  type Material,
  type Object3D,
  type Texture,
} from 'three/webgpu';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { regionAssetBase, type RegionSidecar } from '@stillsun/shared';
import { loadTiledTexture, triplanarColor } from './scene.js';
import { lanternGlassMaterial } from './materials.js';
import { downsMaterial, paintSplat, regolithMaterial, terrainPaletteFor } from './terrain.js';
import { renderFlags } from './renderer.js';

/**
 * The Blender-built region GLBs, one per map (`tools/assetgen/blender/*_region.py` ->
 * `assets/gen/region/<map>_region.glb`), plus the Spine's second GLB for the wreck itself.
 *
 *  - **region**: the sculpted terrain plus the set dressing, merged to ONE MESH PER SOURCE
 *    TEXTURE ATLAS. That merge is the whole perf story: the static world's budget is <= 8 render
 *    objects (`docs/PERF-BUDGET.md`), and the packs each ship a single gradient atlas, so a city
 *    of four hundred Kenney pieces costs one draw, not four hundred.
 *  - **wreck** (the Spine only): the deck kit dressed on its exact collider surfaces.
 *
 * Contract with the rest of the render path:
 *  - The `terrain` mesh is a finer resample of the SAME heightfield the sim walks (the sidecar,
 *    applied to the `MapDef` at boot in main.ts), so it replaces `buildTerrainMesh`'s sheet
 *    outright; feet neither float nor sink. It keeps the tuned material from terrain.ts, with the
 *    GLB's vertex paint multiplied in.
 *  - Everything else is materialled by mesh NAME (see `regionMaterial`), because the name is the
 *    material class the Blender pass merged by. A pack mesh keeps its own atlas texture - that
 *    atlas *is* the gradient atlas the low-poly bible asks for - re-hosted on a node material so
 *    the WebGPU path gets one predictable pipeline per class.
 *  - Fail-soft, like props.ts: a missing or broken GLB resolves `null` and scene.ts keeps the
 *    heightfield mesh / greybox batches and draws the region colliders as crates. `?region=0`
 *    forces that path.
 *
 * Every GLB is meshopt-compressed by Blender's exporter (`EXT_meshopt_compression`), hence the
 * decoder on the loader; the extension awaits `MeshoptDecoder.ready` itself.
 */

/** Maps whose region GLB is prefetched at boot. Adding a map here is the only wiring it needs. */
const REGION_MAPS = ['spine', 'lanternfall', 'vigil-downs'] as const;

/** The Spine alone has a second GLB: the ship. */
const WRECK_URL = '/assets/gen/region/spine_wreck.glb';
const ROCK_TEXTURE_PATH = '/assets/gen/textures/regolith-ground.png';
const HULL_TEXTURE_PATH = '/assets/gen/textures/hull-plating-scorched.png';

/** The Choir Deck's light: the Hymns' magenta-violet (docs/ART-DIRECTION.md, colour story). */
const WOUND_VIOLET = 0xb060ff;
const LANTERN_WARM = 0xffa040;
const LANTERN_COLD = 0xdfe8ff;
/** Bonelight's lantern mint (#1F8F5E), lifted to an emissive that reads at night. */
const LANTERN_MINT = 0x2fd98c;
/** Bonelight's bone (#FBF7EC) and water (#8BA69D). */
const BONE = 0xfbf7ec;
const WATER = 0x8ba69d;
/**
 * Lanternfall's paving. Bonelight's road anchor is #B8B09A and that is what this started at; a
 * step darker in practice, because the noon key lifts a 0.72-albedo ground to near-white and the
 * plaza then reads as a sheet of blown-out paper with the town floating on it. Sat one value
 * below the buildings, the square reads as stone and the eye goes to the Lantern Hall, which is
 * where a player standing at the gate needs it to go.
 */
const PAVING = 0x9a9384;

/**
 * The parsed sidecars, by map id, for the render layer.
 *
 * `main.ts` fetches and validates these to re-author the shared `MapDef`s (that is the sim's
 * business), but three of the lists in them are purely visual — `lanterns`, `benches` and
 * `grassMask` — and would be dead weight on a `MapDef` that the server also holds and that
 * `packages/shared` would then have to justify. So they are handed here instead, once, and
 * `render/life.ts` and the grass reader take them off this store rather than through the map.
 *
 * Set before the first `setMap` for a given map (main.ts awaits the sidecars before deploying);
 * a map with no sidecar simply never appears, and every reader falls back.
 */
const sidecars = new Map<string, RegionSidecar>();

/** Records a map's parsed sidecar for the render layer. Called once per map, at boot. */
export function setRegionSidecar(mapId: string, region: RegionSidecar): void {
  sidecars.set(mapId, region);
}

/** A map's sidecar, or null when Blender has never run for it. */
export function regionSidecar(mapId: string): RegionSidecar | null {
  return sidecars.get(mapId) ?? null;
}

let wreckTemplate: Promise<Object3D | null> | null = null;
let wreckReadyObj: Object3D | null = null;
const regionTemplates = new Map<string, Promise<Object3D | null>>();
const regionReadySet = new Map<string, Object3D | null>();

function loadOne(url: string): Promise<Object3D | null> {
  return new GLTFLoader()
    .setMeshoptDecoder(MeshoptDecoder)
    .loadAsync(url)
    .then((gltf) => gltf.scene)
    .catch((err: unknown) => {
      console.warn(`[stillsun] region asset unavailable: ${url}`, err);
      return null;
    });
}

function loadRegionTemplate(mapId: string): Promise<Object3D | null> {
  let p = regionTemplates.get(mapId);
  if (!p) {
    p = loadOne(`${regionAssetBase(mapId)}.glb`).then((o) => {
      regionReadySet.set(mapId, o);
      return o;
    });
    regionTemplates.set(mapId, p);
  }
  return p;
}

function loadWreckTemplate(): Promise<Object3D | null> {
  if (!wreckTemplate) {
    wreckTemplate = loadOne(WRECK_URL).then((o) => {
      wreckReadyObj = o;
      return o;
    });
  }
  return wreckTemplate;
}

/** Starts every region GLB download at boot so a later `setMap` can attach it synchronously. */
export function prefetchRegions(): void {
  if (!renderFlags.region) return;
  for (const id of REGION_MAPS) void loadRegionTemplate(id);
  void loadWreckTemplate();
}

/** True once this map's GLB has settled, i.e. `attachRegion` resolves without waiting. */
export function regionReady(mapId: string): boolean {
  return regionReadySet.has(mapId);
}

/** True once the wreck GLB has landed *and* loaded: scene.ts can skip its greybox batches. */
export function spineWreckReady(): boolean {
  return wreckReadyObj != null;
}

/** The greybox kit's own material instances, so the wreck's kit meshes reuse them exactly. */
export interface KitMaterials {
  hull: MeshStandardNodeMaterial;
  floor: MeshStandardNodeMaterial;
  ceiling: MeshStandardNodeMaterial;
  crate: MeshStandardNodeMaterial;
  hazard: MeshStandardNodeMaterial;
}

export interface RegionOptions {
  low: boolean;
  kit: KitMaterials;
}

export interface RegionAttachment {
  /** Terrain + dressing, or null if that GLB failed. */
  region: Group | null;
  /** The Spine's wreck, or null on every other map (and if that GLB failed). */
  wreck: Group | null;
}

/** Back-compat alias for the Spine-shaped attachment scene.ts destructures. */
export type SpineRegionAttachment = RegionAttachment;

/**
 * A pack mesh's own atlas, re-hosted on a node material. The Kenney/KayKit packs each bake their
 * whole palette into one small texture sampled by UV islands - which is exactly the gradient
 * atlas `docs/LOW-POLY-BIBLE.md` prescribes - so the right thing to do with a pack material is to
 * keep its map and throw away everything else (no PBR, no specular, no metalness).
 */
function atlasMaterial(mesh: Mesh): MeshStandardNodeMaterial {
  const source = mesh.material;
  const first = Array.isArray(source) ? source[0] : source;
  const map = (first as { map?: Texture | null } | undefined)?.map ?? null;
  const mat = new MeshStandardNodeMaterial({ roughness: 1, metalness: 0 });
  if (map) mat.map = map;
  else mat.color = new Color(0xb0ac97);
  // The Kenney Nature pack ships no atlas at all - thirteen flat material factors - so both
  // region scripts bake its colours into `COLOR_0` and export it as one material. Without
  // `vertexColors` that whole mesh collapses to a single base tone and every tree, stump and rock
  // in the zone comes out the same green. Driven off the geometry rather than the map id so a
  // textured mesh that also carries vertex paint gets both.
  if (mesh.geometry.hasAttribute('color')) mat.vertexColors = true;
  return mat;
}

/**
 * A city square is not a landscape. Lanternfall's ground is one flat paved tone (Bonelight's road
 * `#B8B09A`) rather than the Spine's regolith or the Downs' four-splat heath: a paving slab that
 * changed colour with height and slope, on a field that is height 0 everywhere, would only ever
 * be one colour anyway - bought at the cost of the whole splat shader and a vertex attribute the
 * city's GLB has no reason to carry.
 */
function terrainMaterialFor(mapId: string, low: boolean): MeshStandardNodeMaterial {
  if (mapId === 'vigil-downs') return downsMaterial(low);
  if (mapId === 'lanternfall')
    return new MeshStandardNodeMaterial({ color: new Color(PAVING), roughness: 1, metalness: 0 });
  return regolithMaterial(low, true);
}

/**
 * Node materials for a region's dressing meshes, by the mesh names the Blender scripts assign.
 * Built per attach (not cached module-wide) so `?tex=0` and the tier are honoured, and so a map
 * switch disposes nothing another map still uses.
 */
function regionMaterial(mesh: Mesh): MeshStandardNodeMaterial {
  // Blender uniquifies object names by suffixing `.001` when a name is already taken, and a
  // script that joins one mesh per atlas hits that as soon as an intermediate object shares the
  // final name. The suffix carries no meaning here and an exact-match switch that misses it
  // silently drops a whole material class into the default - which is how Lanternfall's lanterns
  // first rendered as dark slabs.
  const name = mesh.name.replace(/\.\d+$/, '');
  switch (name) {
    // --- the Spine's exterior dressing (its script names meshes by material, not by atlas) ---
    case 'rocks': {
      // Same regolith PNG as the ground, tiled tight and darkened: a rock is the dune's own
      // material with the dust blown off.
      const mat = new MeshStandardNodeMaterial({ color: 0x3a3128, roughness: 0.92, metalness: 0 });
      if (renderFlags.textures) {
        try {
          mat.colorNode = triplanarColor(loadTiledTexture(ROCK_TEXTURE_PATH), 3).mul(0.78);
        } catch (err) {
          console.warn('[stillsun] region rock texture unavailable, using flat material', err);
        }
      }
      return mat;
    }
    case 'hull_debris': {
      const mat = new MeshStandardNodeMaterial({
        color: 0x4e5257,
        roughness: 0.55,
        metalness: 0.6,
      });
      if (renderFlags.textures) {
        try {
          mat.colorNode = triplanarColor(loadTiledTexture(HULL_TEXTURE_PATH), 6);
        } catch (err) {
          console.warn('[stillsun] region hull texture unavailable, using flat material', err);
        }
      }
      return mat;
    }
    case 'pylons':
      return new MeshStandardNodeMaterial({ color: 0x3d342c, roughness: 0.7, metalness: 0.5 });
    case 'plinths':
      return new MeshStandardNodeMaterial({ color: 0x6b6154, roughness: 0.9, metalness: 0 });
    case 'glass':
      // Fused regolith: pale amber glass, glossy enough to throw the Still back at the player.
      return new MeshStandardNodeMaterial({ color: 0xd1bd99, roughness: 0.18, metalness: 0.1 });

    // --- Lanternfall / the Vigil Downs: one mesh per source pack atlas ---
    case 'town':
    case 'dungeon':
    case 'nature':
      return atlasMaterial(mesh);

    // --- authored in Blender, not from a pack ---
    case 'cloth':
      // Banners. Flat bone, double-sided, no shine; `render/life.ts` gives it the vertex wave.
      return new MeshStandardNodeMaterial({
        color: new Color(BONE),
        roughness: 1,
        metalness: 0,
        side: 2,
      });
    case 'lantern':
      // Bonelight's mint. `render/life.ts` flickers this material's `emissiveIntensity` - and
      // nothing else: PERF-BUDGET rule 3 forbids changing the light set at runtime.
      return lanternGlassMaterial(LANTERN_MINT, 1.7);
    case 'water':
      return new MeshStandardNodeMaterial({
        color: new Color(WATER),
        roughness: 0.25,
        metalness: 0,
        transparent: true,
        opacity: 0.72,
      });

    default:
      // An atlas split the script had to report (`town_a`, `nature_b`, ...) still lands here with
      // its own map attached, so an unexpected name degrades to the right picture, not to grey.
      if (/^(town|dungeon|nature)_/.test(name)) return atlasMaterial(mesh);
      return new MeshStandardNodeMaterial({ color: 0x4a4038, roughness: 0.8, metalness: 0.1 });
  }
}

/**
 * Materials for the wreck's meshes. The kit meshes *share* scene.ts's instances (returned as-is,
 * never disposed here); the rest are built fresh per attach and disposed with the group.
 */
function wreckMaterial(name: string, kit: KitMaterials, fresh: Material[]): Material {
  const own = (m: MeshStandardNodeMaterial): MeshStandardNodeMaterial => {
    fresh.push(m);
    return m;
  };
  switch (name) {
    case 'kit_hull':
    case 'shell':
      return kit.hull;
    case 'kit_floor':
      return kit.floor;
    case 'kit_ceiling':
      // Dark hull overhead with lantern pools - NOT the greybox's amber seam-grid ceiling, which
      // read as a glowing lattice over every deck. The only emissives on a ceiling are the
      // Choir wound's violet rim and the lantern glass itself.
      return own(
        new MeshStandardNodeMaterial({ color: 0x14171a, roughness: 0.85, metalness: 0.4 }),
      );
    case 'kit_crates':
      return kit.crate;
    case 'props':
      // Pews, the lectern, the bunk, bedrolls, the camp tarp: scrap timber and canvas, dark and
      // matte, so the nave reads as furniture rather than as a cargo stack.
      return own(
        new MeshStandardNodeMaterial({ color: 0x3e3028, roughness: 0.88, metalness: 0.04 }),
      );
    case 'hazard':
      return kit.hazard;
    case 'fixtures':
      // Cages, chains, conduit, rails, posts: dark oxidised iron, one flat material.
      return own(
        new MeshStandardNodeMaterial({ color: 0x2a2624, roughness: 0.7, metalness: 0.65 }),
      );
    case 'lantern_warm':
      return own(lanternGlassMaterial(LANTERN_WARM, 1.6));
    case 'lantern_cold':
      return own(lanternGlassMaterial(LANTERN_COLD, 1.8));
    case 'wound':
      // A flat slab, not a lantern: the glass material's fresnel rim lights a whole large face
      // and read as two bright violet plates from the ridge. Plain emissive instead.
      return own(
        new MeshStandardNodeMaterial({
          color: 0x120a18,
          emissive: new Color(WOUND_VIOLET),
          emissiveIntensity: 1.8,
          roughness: 0.6,
          metalness: 0,
        }),
      );
    case 'drift':
      // Sand drifted against the hull's skirt: the regolith's colour, matte.
      return own(new MeshStandardNodeMaterial({ color: 0x4a3c30, roughness: 1, metalness: 0 }));
    case 'paper':
      return own(new MeshStandardNodeMaterial({ color: 0xb8a98a, roughness: 0.95, metalness: 0 }));
    default:
      return own(new MeshStandardNodeMaterial({ color: 0x4a4038, roughness: 0.8, metalness: 0.1 }));
  }
}

/**
 * Ready-to-parent copies of one map's region (and, on the Spine, the wreck), with geometry shared
 * with the templates, the terrain re-materialled from terrain.ts, the dressing given this scene's
 * node materials and the wreck's kit meshes given the kit's own material instances. Either half
 * is `null` if its GLB never loaded. Resolves immediately once `regionReady(mapId)`.
 */
export async function attachRegion(mapId: string, opts: RegionOptions): Promise<RegionAttachment> {
  if (!renderFlags.region) return { region: null, wreck: null };
  const [tplRegion, tplWreck] = await Promise.all([
    loadRegionTemplate(mapId),
    mapId === 'spine' ? loadWreckTemplate() : Promise.resolve(null),
  ]);
  let region: Group | null = null;
  let wreck: Group | null = null;

  if (tplRegion) {
    region = new Group();
    region.name = `${mapId}-region`;
    const copy = tplRegion.clone(true);
    const disposable: Material[] = [];
    copy.traverse((o) => {
      // Duck-typed, not `instanceof`: this subtree came in through GLTFLoader (see props.ts).
      const mesh = o as Mesh;
      if (!mesh.isMesh) return;
      if (mesh.name === 'terrain') {
        // The terrain material blends four layers by a `splat` vertex attribute; when the GLB
        // does not carry one, paint it from the sheet's own positions and normals before the
        // material asks. `clone(true)` shares geometry with the template, so this both writes
        // once (a second attach finds the attribute already there) and is why it is idempotent.
        //
        // The palette is per map, and getting it wrong is not cosmetic: under `'downs'`
        // `paintSplat` *adopts* a four-component `color` attribute as authored weights (R grass,
        // G heath, B barrow earth, A wet stone - `downs_region.py`'s contract), whereas the
        // Spine's GLB carries `color` as `regolithMaterial`'s vertex tint. Passing the wrong one
        // reads zone paint as splat weights and repaints the whole map.
        // Lanternfall's paving needs no splat at all; painting one would only write an attribute
        // nothing samples onto 1,089 vertices at every attach.
        if (mapId !== 'lanternfall' && !mesh.geometry.hasAttribute('splat')) {
          paintSplat(mesh.geometry, terrainPaletteFor(mapId));
        }
        mesh.material = terrainMaterialFor(mapId, opts.low);
        mesh.receiveShadow = true;
        // Receive only, for the same reason as the heightfield sheet in scene.ts: a low sun
        // already bakes the self-shadowing into the displaced normals, and the shadow pass over
        // tens of thousands of triangles would buy nothing but fill.
        mesh.castShadow = false;
      } else {
        const glow = mesh.name === 'lantern';
        mesh.material = regionMaterial(mesh);
        mesh.castShadow = !glow && mesh.name !== 'cloth' && mesh.name !== 'water';
        mesh.receiveShadow = !glow;
      }
      disposable.push(mesh.material as Material);
    });
    region.add(copy);
    region.userData['dispose'] = () => {
      for (const m of disposable) m.dispose();
    };
  }

  if (tplWreck) {
    wreck = new Group();
    wreck.name = 'spine-wreck';
    const copy = tplWreck.clone(true);
    const fresh: Material[] = [];
    copy.traverse((o) => {
      const mesh = o as Mesh;
      if (!mesh.isMesh) return;
      mesh.material = wreckMaterial(mesh.name, opts.kit, fresh);
      // Emissive glass and paper never cast (the strips are sub-5 cm and would only add
      // shadow-pass draws); everything structural casts and receives.
      const glow =
        mesh.name === 'lantern_warm' || mesh.name === 'lantern_cold' || mesh.name === 'wound';
      mesh.castShadow = !glow && mesh.name !== 'paper' && mesh.name !== 'hazard';
      mesh.receiveShadow = !glow;
    });
    wreck.add(copy);
    wreck.userData['dispose'] = () => {
      for (const m of fresh) m.dispose();
    };
  }

  return { region, wreck };
}
